/**
 * Arthaus artist page — section index (Phase 10, HD-AR-EXHIBITION / HD-AR-TITLEWALL)
 * docs/storefront-redesign/requirements/6_artist_profile/HIGH_DESIGN_TRD.md
 *
 * Markup: snippets/exhibition-room-header.liquid ([data-hd-exhibit-room]) in each artist section,
 * rendered server-side when the scrolling artist layout setting is on.
 *
 *  1. Builds the section index from the headings in DOM order (sections that render nothing
 *     simply don't appear): a slim rail fixed in the left margin at ≥1440px, and an inline
 *     list above the works below that, which becomes the sticky bar on scroll. Plain labels,
 *     no numbering: home decor, not a gallery show.
 *  2. Scroll-spy: the section nearest the viewport's middle gets aria-current="true".
 *  3. Clicking a section scrolls to it and moves focus to its heading.
 *
 * ?hd=-artist-exhibition (or the flag off) skips the index. Reduced motion: instant scroll,
 * no rail fade.
 */
(function () {
  'use strict';

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function track(action, props) {
    if (typeof window.ahPosthog === 'function') window.ahPosthog('hd_ar', action, props || {});
  }

  function init() {
    // Title-wall CTA analytics (works on any layout that renders them).
    document.querySelectorAll('[data-hd-titlewall-cta]').forEach(function (a) {
      a.addEventListener('click', function () { track('titlewall_cta', { cta: a.getAttribute('data-hd-titlewall-cta') }); });
    });

    var rooms = Array.prototype.slice.call(document.querySelectorAll('[data-hd-exhibit-room]'));
    if (!rooms.length) return;

    if (!document.documentElement.hasAttribute('data-hd-artist-exhibition') || rooms.length < 2) return;

    // Section index: one list builder, two placements. Plain labels, no numbering: the page
    // reads as home decor, not a gallery show (memory: brand-home-decor-not-gallery).
    function buildList(className) {
      var ol = document.createElement('ol');
      ol.className = className;
      rooms.forEach(function (room) {
        var li = document.createElement('li');
        var a = document.createElement('a');
        a.href = '#' + room.id;
        a.setAttribute('data-hd-exhibit-room-link', room.id);
        a.innerHTML = '<span class="hd-room-tick" aria-hidden="true"></span><span>' +
          escapeHtml(room.getAttribute('data-hd-exhibit-room-title') || '') + '</span>';
        li.appendChild(a);
        ol.appendChild(li);
      });
      return ol;
    }

    var rail = document.createElement('nav');
    rail.setAttribute('aria-label', 'On this page');
    rail.className = 'hidden min-[1440px]:block fixed left-6 top-1/2 -translate-y-1/2 -translate-x-3 z-sticky-bar w-28 opacity-0 pointer-events-none' +
      (reduce ? '' : ' transition-[opacity,transform] duration-[var(--dur-gentle)] ease-gallery');
    rail.setAttribute('inert', '');
    rail.appendChild(buildList('flex flex-col gap-3 font-mono text-[11px] leading-snug tracking-[0.08em] uppercase'));
    document.body.appendChild(rail);

    var STRIP = 'flex flex-nowrap gap-x-5 overflow-x-auto whitespace-nowrap px-[var(--gutter-x)] font-mono text-[10px] tracking-[0.08em] uppercase [scrollbar-width:none] [&::-webkit-scrollbar]:hidden';
    var inline = document.querySelector('[data-hd-exhibit-room-index-inline]');
    if (inline) {
      inline.appendChild(buildList(STRIP + ' -mx-[var(--gutter-x)] pb-1'));
      inline.hidden = false;
    }

    // Sticky room bar (below 1440px, where the margin rail doesn't fit): once the inline strip
    // scrolls up under the site header, a slim copy slides down from beneath the header and
    // stays pinned there. It follows the reader: the current room lights up, the strip glides
    // to keep it centred, and a hairline fills with progress through the exhibition.
    var bar = null, barList = null, barProgress = null;
    var wide = window.matchMedia ? window.matchMedia('(min-width: 1440px)') : { matches: false };
    if (inline) {
      bar = document.createElement('nav');
      bar.setAttribute('aria-label', 'On this page');
      bar.className = 'min-[1440px]:hidden fixed inset-x-0 z-sticky-bar bg-parchment/95 backdrop-blur-sm border-b border-border/70 -translate-y-full opacity-0 pointer-events-none' +
        (reduce ? '' : ' transition-[opacity,transform] duration-[var(--dur-base)] ease-gallery');
      bar.setAttribute('inert', '');
      barList = buildList(STRIP + ' py-3');
      bar.appendChild(barList);
      barProgress = document.createElement('span');
      barProgress.setAttribute('aria-hidden', 'true');
      barProgress.className = 'absolute left-0 bottom-0 h-px w-full bg-bronze origin-left scale-x-0';
      bar.appendChild(barProgress);
      document.body.appendChild(bar);
    }
    var siteHeader = document.querySelector('[data-header]');
    function headerBottom() {
      var b = siteHeader ? siteHeader.getBoundingClientRect().bottom : 0;
      return b > 0 ? b : 0;
    }

    var links = Array.prototype.slice.call(document.querySelectorAll('[data-hd-exhibit-room-link]'));
    links.forEach(function (a) {
      a.classList.add('group/room', 'flex', 'items-center', 'gap-2', 'shrink-0', 'whitespace-nowrap', 'text-warm-gray', 'hover:text-charcoal', 'transition-colors');
      var tick = a.querySelector('.hd-room-tick');
      tick.className = 'hd-room-tick block h-px w-0 bg-bronze transition-[width] duration-[var(--dur-quick)]';
      a.addEventListener('click', function (e) {
        var target = document.getElementById(a.getAttribute('data-hd-exhibit-room-link'));
        if (!target) return;
        e.preventDefault();
        // Land the heading just below the site header (and the sticky room bar, when shown).
        var offset = headerBottom() + (bar && !wide.matches ? bar.offsetHeight : 0) + 16;
        window.scrollTo({ top: window.scrollY + target.getBoundingClientRect().top - offset, behavior: reduce ? 'auto' : 'smooth' });
        target.focus({ preventScroll: true });
        try { history.replaceState(null, '', '#' + target.id); } catch (err) { /* sandboxed */ }
        track('index_click', { room_key: target.getAttribute('data-hd-exhibit-room') });
      });
    });

    // 3. Scroll-spy.
    var active = null;
    function setActive(id) {
      if (id === active) return;
      active = id;
      links.forEach(function (a) {
        var on = a.getAttribute('data-hd-exhibit-room-link') === id;
        if (on) a.setAttribute('aria-current', 'true'); else a.removeAttribute('aria-current');
        a.classList.toggle('text-charcoal', on);
        a.classList.toggle('text-warm-gray', !on);
        var tick = a.querySelector('.hd-room-tick');
        tick.classList.toggle('w-3', on);
        tick.classList.toggle('w-0', !on);
      });
      // Keep the current room centred in the sticky bar's strip (scrolls the strip, not the page).
      if (id && barList) {
        var cur = barList.querySelector('[data-hd-exhibit-room-link="' + id + '"]');
        if (cur) {
          var left = cur.offsetLeft - (barList.clientWidth - cur.offsetWidth) / 2;
          barList.scrollTo({ left: Math.max(0, left), behavior: reduce ? 'auto' : 'smooth' });
        }
      }
      if (id) {
        var i = rooms.findIndex(function (r) { return r.id === id; });
        track('room_view', { room_index: i + 1, room_key: rooms[i].getAttribute('data-hd-exhibit-room') });
      }
    }
    // A room "owns" the page from its heading down to the next heading.
    function spy() {
      var mid = window.innerHeight * 0.45, current = null;
      for (var i = 0; i < rooms.length; i++) {
        if (rooms[i].getBoundingClientRect().top <= mid) current = rooms[i].id; else break;
      }
      setActive(current);
    }

    // Rail visibility: after the title wall is half out of view, and not over the footer.
    var hero = document.querySelector('[data-hd-titlewall]') || document.querySelector('[data-hd-split-hero]') || document.querySelector('.artist-hero-section');
    var footer = document.querySelector('footer');
    function railVisibility() {
      var show = true;
      if (hero) { var hr = hero.getBoundingClientRect(); show = hr.bottom < hr.height / 2; }
      if (show && footer) show = footer.getBoundingClientRect().top > window.innerHeight * 0.6;
      if (show && rooms[0].getBoundingClientRect().top > window.innerHeight) show = false;
      rail.classList.toggle('opacity-0', !show);
      rail.classList.toggle('-translate-x-3', !show); // slides in from the margin
      rail.classList.toggle('pointer-events-none', !show);
      if (show) rail.removeAttribute('inert'); else rail.setAttribute('inert', '');
    }

    // Sticky bar: shown once the inline strip has scrolled under the header, hidden near the
    // footer. Pinned to the header's live bottom edge; the hairline tracks exhibition progress.
    function barUpdate() {
      if (!bar) return;
      var top = headerBottom();
      bar.style.top = top + 'px';
      var show = !wide.matches && inline.getBoundingClientRect().bottom < top;
      if (show && footer) show = footer.getBoundingClientRect().top > top + bar.offsetHeight + 40;
      bar.classList.toggle('opacity-0', !show);
      bar.classList.toggle('-translate-y-full', !show);
      bar.classList.toggle('pointer-events-none', !show);
      if (show) bar.removeAttribute('inert'); else bar.setAttribute('inert', '');
      if (show) {
        var start = rooms[0].getBoundingClientRect().top + window.scrollY - top;
        var end = (footer ? footer.getBoundingClientRect().top + window.scrollY : document.documentElement.scrollHeight) - window.innerHeight;
        var p = end > start ? Math.min(1, Math.max(0, (window.scrollY - start) / (end - start))) : 0;
        barProgress.style.transform = 'scaleX(' + p.toFixed(4) + ')';
      }
    }

    var queued = false;
    function onScroll() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () { queued = false; spy(); railVisibility(); barUpdate(); });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    onScroll();
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
